// MARK://dhk8/pos/features/billing/billing-detail-page
// Una factura CFDI 4.0: encabezado fiscal, conceptos, totales, estatus CFDI/SAT y
// acción de cancelación. Ported from sc-restaurant's InvoiceDetailPage logic.
//
// Wired to clients/invoices + clients/cfdi (stub-mode in dev). Stamp/cancel hit
// dhk8-fiscal-cfdi (TODO backend — see clients/cfdi.ts).

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DataTable, type ColumnDef } from '../../components/DataTable.js'
import { cancelInvoice, getInvoice, type CancellationMotivo, type InvoiceDto, type InvoiceItemDto } from '../../lib/clients/invoices.js'
import { getCfdiStatus, type CfdiStatusDto } from '../../lib/clients/cfdi.js'
import { formatCents } from '../../lib/money.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { StatusBadge } from './StatusBadge.js'
import { billingStyles } from './billing-styles.js'

const MOTIVO_OPTS: Array<{ value: CancellationMotivo; label: string }> = [
  { value: '01', label: '01 — Comprobante con errores con relación' },
  { value: '02', label: '02 — Comprobante con errores sin relación' },
  { value: '03', label: '03 — No se llevó a cabo la operación' },
  { value: '04', label: '04 — Operación nominativa en factura global' },
]

export default function BillingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [invoice, setInvoice] = useState<InvoiceDto | null>(null)
  const [cfdi, setCfdi] = useState<CfdiStatusDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [motivo, setMotivo] = useState<CancellationMotivo>('02')
  const [replacementUuid, setReplacementUuid] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!id) return
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    getInvoice(id, ctrl.signal)
      .then(async (inv) => {
        setInvoice(inv)
        if (inv) {
          const status = await getCfdiStatus(inv.id, { uuid: inv.cfdiUuid, cancelled: inv.status === 'CANCELLED' }, ctrl.signal).catch(() => null)
          setCfdi(status)
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar la factura'))
      .finally(() => setLoading(false))
    return () => ctrl.abort()
  }, [id])

  const itemColumns: ColumnDef<InvoiceItemDto>[] = useMemo(
    () => [
      { key: 'description', label: 'Concepto' },
      { key: 'quantity', label: 'Cant.', numeric: true, align: 'end', hideOnNarrow: true },
      { key: 'unitPriceCents', label: 'P. unitario', numeric: true, align: 'end', hideOnNarrow: true },
      { key: 'taxCents', label: 'IVA', numeric: true, align: 'end', hideOnNarrow: true },
      { key: 'totalCents', label: 'Importe', numeric: true, align: 'end' },
    ],
    [],
  )

  const renderItemCell = (it: InvoiceItemDto, c: ColumnDef<InvoiceItemDto>) => {
    switch (c.key) {
      case 'description':
        return (
          <span>
            {it.description}
            <br />
            <small style={{ color: 'var(--pos-fg-tertiary, var(--text-tertiary, #7E8C82))' }}>SAT {it.satProductCode} · {it.satUnitCode}</small>
          </span>
        )
      case 'quantity':
        return String(it.quantity)
      case 'unitPriceCents':
        return <span className="dhk8-bil__money">{formatCents(it.unitPriceCents)}</span>
      case 'taxCents':
        return <span className="dhk8-bil__money">{formatCents(it.taxCents)}</span>
      case 'totalCents':
        return <span className="dhk8-bil__money">{formatCents(it.totalCents)}</span>
      default:
        return null
    }
  }

  async function doCancel() {
    if (!invoice) return
    if (!(await confirm({
      title: `Cancelar factura ${invoice.serie}-${invoice.folio}`,
      message: 'Esta acción se reporta al SAT.',
      confirmLabel: 'Cancelar factura',
      cancelLabel: 'No',
      tone: 'danger',
    }))) return
    setBusy(true)
    setError(null)
    try {
      const updated = await cancelInvoice(invoice.id, motivo, replacementUuid || undefined)
      setInvoice(updated)
      const status = await getCfdiStatus(updated.id, { uuid: updated.cfdiUuid, cancelled: true }).catch(() => null)
      setCfdi(status)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cancelar la factura')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dhk8-bil">
      <button className="dhk8-bil__back" onClick={() => navigate('/billing')}>‹ Volver a facturas</button>

      {loading && <div className="dhk8-bil__notice">Cargando factura…</div>}
      {error && <div className="dhk8-bil__error">{error}</div>}
      {!loading && !invoice && !error && <div className="dhk8-bil__notice">Factura no encontrada.</div>}

      {invoice && (
        <>
          <header className="dhk8-bil__head">
            <h1 className="dhk8-bil__title">Factura {invoice.serie}-{invoice.folio} <StatusBadge status={invoice.status} /></h1>
            <p className="dhk8-bil__sub">{invoice.isGlobalDay ? 'Factura global del día' : `Tipo ${invoice.type}`} · emitida {new Date(invoice.issuedAt).toLocaleString('es-MX')}</p>
          </header>

          <div className="dhk8-bil__cols">
            <section className="dhk8-bil__card">
              <h2>Datos fiscales</h2>
              <table className="dhk8-bil__kv">
                <tbody>
                  <tr><td>Receptor</td><td>{invoice.customer ? invoice.customer.legalName : 'Público en general'}</td></tr>
                  <tr><td>RFC</td><td><code>{invoice.customer?.rfc ?? 'XAXX010101000'}</code></td></tr>
                  <tr><td>Uso CFDI</td><td>{invoice.cfdiUsage}</td></tr>
                  <tr><td>Método de pago</td><td>{invoice.paymentMethod}</td></tr>
                  <tr><td>Forma de pago</td><td>{invoice.paymentForm}</td></tr>
                  <tr><td>Moneda</td><td>{invoice.currency}</td></tr>
                  {invoice.orderId && <tr><td>Orden</td><td><code>{invoice.orderId}</code></td></tr>}
                  {invoice.notes && <tr><td>Notas</td><td>{invoice.notes}</td></tr>}
                </tbody>
              </table>
            </section>

            <section className="dhk8-bil__card">
              <h2>Estatus CFDI / SAT</h2>
              <table className="dhk8-bil__kv">
                <tbody>
                  <tr><td>Estado SAT</td><td>{cfdi?.satStatus ?? (invoice.cfdiUuid ? 'STAMPED' : 'NOT_STAMPED')}</td></tr>
                  <tr><td>UUID</td><td>{invoice.cfdiUuid ? <code style={{ fontSize: 'var(--pos-text-xxs, 11px)' }}>{invoice.cfdiUuid}</code> : '— sin timbrar —'}</td></tr>
                  {invoice.cfdiStampedAt && <tr><td>Timbrada</td><td>{new Date(invoice.cfdiStampedAt).toLocaleString('es-MX')}</td></tr>}
                  {invoice.cfdiCancelledAt && <tr><td>Cancelada</td><td>{new Date(invoice.cfdiCancelledAt).toLocaleString('es-MX')} (motivo {invoice.cfdiCancellationMotivo})</td></tr>}
                  {cfdi?.verifyUrl && <tr><td>Verificar</td><td><a href={cfdi.verifyUrl} target="_blank" rel="noreferrer">SAT verificación</a></td></tr>}
                  {cfdi?.xmlUrl && <tr><td>Archivos</td><td><a href={cfdi.xmlUrl} target="_blank" rel="noreferrer">XML</a>{cfdi.pdfUrl ? <> · <a href={cfdi.pdfUrl} target="_blank" rel="noreferrer">PDF</a></> : null}</td></tr>}
                  {cfdi?.errorMessage && <tr><td>Error</td><td style={{ color: 'var(--pos-danger, var(--status-error, #E5484D))' }}>{cfdi.errorMessage}</td></tr>}
                </tbody>
              </table>
            </section>

            <section className="dhk8-bil__card">
              <h2>Totales</h2>
              <table className="dhk8-bil__kv">
                <tbody>
                  <tr><td>Subtotal</td><td className="dhk8-bil__money">{formatCents(invoice.subtotalCents)}</td></tr>
                  <tr><td>Descuento</td><td className="dhk8-bil__money">{formatCents(invoice.discountCents)}</td></tr>
                  <tr><td>IVA</td><td className="dhk8-bil__money">{formatCents(invoice.taxCents)}</td></tr>
                  <tr><td><strong>Total</strong></td><td className="dhk8-bil__money"><strong>{formatCents(invoice.totalCents)}</strong></td></tr>
                </tbody>
              </table>
            </section>
          </div>

          <section className="dhk8-bil__card">
            <h2>Conceptos</h2>
            <DataTable
              columns={itemColumns}
              rows={invoice.items}
              getRowKey={(it) => it.id}
              renderCell={renderItemCell}
              pageSize={0}
              primaryKey="description"
              ariaLabel="Conceptos de la factura"
              mobileCards={false}
            />
          </section>

          {invoice.status !== 'CANCELLED' && (
            <section className="dhk8-bil__card">
              <h2>Cancelar factura</h2>
              <div className="dhk8-bil__form">
                <label>Motivo SAT<select value={motivo} onChange={(e) => setMotivo(e.target.value as CancellationMotivo)}>{MOTIVO_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
                {motivo === '01' && <label>UUID que sustituye<input value={replacementUuid} onChange={(e) => setReplacementUuid(e.target.value)} placeholder="UUID de la factura de reemplazo" /></label>}
              </div>
              <div className="dhk8-bil__actions">
                <button className="dhk8-bil__btn dhk8-bil__btn--primary" disabled={busy || (motivo === '01' && !replacementUuid)} onClick={doCancel}>{busy ? 'Cancelando…' : 'Cancelar ante el SAT'}</button>
              </div>
            </section>
          )}
        </>
      )}

      <style>{billingStyles}</style>
    </div>
  )
}
